<template>
<div>
  <ProjectForm
    :project="project"
    :projectSaved="projectSaved"
    :indexTab="indexTab"
    :activeTab="activeTab"
    :successDialog="successDialog"
    :validations="validations"
    :isCreateView="isCreateView"
    @go-to-tab="goToTab"
    @change-tab="onChangeTabs"
    @on-save-press="onValid"
    @on-change-files="files = $event"
    @confirm-success-dialog="successDialog = false" >
  </ProjectForm>
  <ErrorDialog
    :showErrorDialog="errorDialog"
    :errorDialogMessage="errorDialogMessage"
    @confirm-dialog="errorDialogConfirm"
    @close-dialog="errorDialogConfirm">
  </ErrorDialog>
  <ChangeTabDialog
    :showDialog="changeTabDialog"
    @save-confirm="updateProject()"
    @save-cancel="onCancelTabChangeDialog()"
    @go-back="onGoBackTabChangeDialog()"
  />
</div>
</template>

<script>
import { required, helpers } from 'vuelidate/lib/validators'
import { mapGetters } from 'vuex'
import { diff } from 'deep-diff'
import { getData } from '@/utils/object.js'
import { clone as cloneArray } from '@/utils/array.js'
import { clone, whenValueIsCeroChangeByMinusOne, whenValueIsMinusOneChangeByCero, formatDate } from '@/utils/form.js'
import { thousandSeparatorFormat } from '@/utils/number.js'
import RedmineService from '@/services/redmine-gateway.js'
import ProjectService from '@/services/projects.js'
import { statisticInitialValue } from '@/components/ProjectForm/EstadisticasForm'
import ProjectForm, { tabList } from '@/components/ProjectForm/ProjectForm'
import ChangeTabDialog from '@/components/ProjectForm/ChangeTabDialog'
import errorDialogMixin from '@/mixins/dialog-for-server-error.js'

const codeValidator = helpers.regex('codeValidator', /^[0-9a-z_-]*$/)

const hasChangedObjectValue = (arrChanges, keyChanged) => arrChanges && arrChanges.find(c => c && c.path.find(p => p === keyChanged))

const initialProjectObj = {
  general: {
    images: []
  },
  fichaTecnica: {
    costo: 0,
    status: {},
    enteEjecutor: [],
    types: []
  },
  estadisticas: clone(statisticInitialValue),
  documentos: [],
  componentes: [],
  vistaSatelital: {
    markerPositions: { },
    shapes: '',
    pin: '',
    cierre: '',
    planificacion: '',
    ejecucion: '',
    tooltip: ''
  }
}

export default {
  components: { ProjectForm, ChangeTabDialog },
  mixins: [ errorDialogMixin ],
  mounted () {
    this.isCreateView = this.$route.meta.isCreate
    this.loadAllDataAndSetCustomValidations()
  },
  data: () => ({
    activeTab: 'general',
    indexTab: 0,
    lastIndexTab: 0,
    project: {},
    projectSaved: {},
    files: {},
    immutableProject: null,
    code: null,
    successDialog: false,
    projectWasUpdatedButNotSaved: false,
    changeTabDialog: false,
    changeTabDialogDefaultFunction: () => null,
    hasChangedColor: false,
    isCreateView: false,
    validations: {
      general: {
        shapes: { }
      }
    }
  }),
  methods: {
    onValid ({ tab, isValid, data }) {
      if (isValid) {
        if (this.isCreateView) {
          this.project[tab] = data
          this.indexTab += 1
          if (tabList[this.indexTab]) {
            this.activeTab = tabList[this.indexTab]
          } else {
            this.createProject()
          }
        } else {
          this.updateProject()
        }
      }
    },
    loadProjectData (project) {
      const projectToUpdate = clone(project)
      const general = {
        code: projectToUpdate.code,
        name: projectToUpdate.name,
        descripcion: projectToUpdate.descripcion,
        slogan: projectToUpdate.slogan,
        number: projectToUpdate.number,
        color: projectToUpdate.color,
        images: projectToUpdate.images,
        whatsapp: projectToUpdate.whatsapp
      }
      const fichaTecnica = {
        ...projectToUpdate.fichaTecnica,
        families: projectToUpdate.families.amount ? thousandSeparatorFormat(projectToUpdate.families.amount) : 0,
        costo: thousandSeparatorFormat(projectToUpdate.costo),
        status: clone(projectToUpdate.status),
        types: clone(projectToUpdate.types)
      }
      if (projectToUpdate.estadisticas) {
        projectToUpdate.estadisticas = whenValueIsMinusOneChangeByCero(projectToUpdate.estadisticas)
      } else {
        projectToUpdate.estadisticas = clone(statisticInitialValue)
      }
      const componentes = cloneArray(project.componentes)
      if (getData(componentes).map) {
        componentes.map((s, i) => {
          if (s.fichaTecnica && Object.values(s.fichaTecnica).length) {
            s.fichaTecnica.costoEstimativo = s.fichaTecnica.costoEstimativo ? thousandSeparatorFormat(s.fichaTecnica.costoEstimativo) : 0
            s.fichaTecnica.status = s.fichaTecnica.status ? s.fichaTecnica.status : (project.fichaTecnica.status || project.status)
          }
          return s
        })
      }
      const vistaSatelital = {
        ...initialProjectObj.vistaSatelital,
        markerPositions: projectToUpdate.markerPositions
      }
      this.hasChangedColor = false
      this.setProjectAndImmutableProject({
        ...project,
        ...projectToUpdate,
        general,
        fichaTecnica,
        componentes,
        vistaSatelital
      }, project)
    },
    setProjectAndImmutableProject (project, immutableProject) {
      this.project = clone(project)
      this.immutableProject = clone(immutableProject)
    },
    getFormDataToSave () {
      let project = clone(this.project)
      if (project.general) {
        project = {
          ...project,
          ...clone(project.general),
          code: project.general.code || this.code
        }
        delete project.general
      }
      if (project.fichaTecnica) {
        if (this.immutableProject) {
          project = {
            ...project,
            families: clone({
              ...this.immutableProject.families,
              amount: project.fichaTecnica.families || 0
            }),
            markerPositions: clone({
              ...this.immutableProject.markerPositions,
              ...project.vistaSatelital.markerPositions
            }),
            status: project.fichaTecnica.status || clone(this.immutableProject.status),
            types: project.fichaTecnica.types || clone(this.immutableProject.types)
          }
        } else {
          project = {
            ...project,
            families: {
              message: '',
              popover: '',
              amount: project.fichaTecnica.families || 0
            },
            markerPositions: clone({
              ...project.vistaSatelital.markerPositions
            }),
            status: project.fichaTecnica.status,
            types: project.fichaTecnica.types
          }
        }
        project = {
          ...project,
          costo: project.fichaTecnica.costo ? thousandSeparatorFormat(project.fichaTecnica.costo) : 0
        }
        delete project.fichaTecnica.families
        delete project.fichaTecnica.costo
        delete project.fichaTecnica.status
        delete project.fichaTecnica.types
        delete project.vistaSatelital.markerPositions
      }
      if (project.estadisticas) {
        project.estadisticas = whenValueIsCeroChangeByMinusOne(project.estadisticas)
      }
      if (project.documentos && this.immutableProject && this.immutableProject.documentos) {
        project.documentos = [ ...this.immutableProject.documentos.filter(doc => !doc.link || !doc.name), ...project.documentos ]
      }
      if (getData(project.componentes).map) {
        project.componentes.map((s, i) => {
          if (s.fichaTecnica && Object.values(s.fichaTecnica).length) {
            s.fichaTecnica.costoEstimativo = s.fichaTecnica.costoEstimativo ? thousandSeparatorFormat(s.fichaTecnica.costoEstimativo) : 0
            s.fichaTecnica.status = s.fichaTecnica.status ? s.fichaTecnica.status : (project.fichaTecnica.status || clone(this.immutableProject.status))
          }
          return s
        })
      }
      if (this.immutableProject) {
        project = {
          ...this.immutableProject,
          ...project
        }
      }
      project.auditoria = this.getAuditoria(project)
      return project
    },
    loadAllDataAndSetCustomValidations () {
      this.code = this.$route.params.code
      if (this.isCreateView) {
        this.project = clone(initialProjectObj)
        this.validations.general = {
          code: { required, codeValidator }
        }
      } else {
        ProjectService.getProjectForFormView(this.$http, this.apiKey, this.code).then(this.loadProjectData).catch(err => {
          if (err.status === 403) return this.showErrorMessage({ defaultMessage: 'Usted no tiene permisos para editar este proyecto', ...err }, () => this.$router.push({ name: 'listProject' }))
          if (!err.status) return this.showErrorMessage({ defaultMessage: 'El servidor no responde o está caído', ...err })
          this.showErrorMessage({ defaultMessage: 'Ocurrió un error al intentar obtener datos de proyectos', ...err })
        })
      }
    },
    setIndexTab (indexTab) {
      this.indexTab = indexTab
      if (tabList[this.indexTab]) {
        this.activeTab = tabList[this.indexTab]
        this.lastIndexTab = this.indexTab
      }
    },
    goToTab (sum) {
      this.setIndexTab(this.indexTab + sum)
    },
    onChangeTabs (tab) {
      this.activeTab = tab
      this.changeTabDialogDefaultFunction = () => {
        this.indexTab = tabList.indexOf(tab)
        this.lastIndexTab = this.indexTab
      }
      this.shouldShowTabChangeDialog()
    },
    createProject () {
      this.saveProject(RedmineService.createProject, this.getFormDataToSave())
    },
    updateProject () {
      this.saveProject(RedmineService.updateProject, this.getFormDataToSave())
    },
    saveProject (savePromiseFunction, projectToSave) {
      savePromiseFunction(this.$http, this.apiKey, { project: projectToSave, files: this.files })
        .then(response => {
          this.successDialog = true
          this.loadProjectData(projectToSave)
          if (this.projectWasUpdatedButNotSaved) this.onCancelTabChangeDialog()
          return response ? response.project : projectToSave
        })
        .then(projectSaved => {
          this.projectSaved = projectSaved
        })
        .catch(err => this.showErrorMessage({ msg: 'Ocurrió un error al intentar guardar proyectos', err }))
    },
    shouldShowTabChangeDialog () {
      if (this.projectWasUpdatedButNotSaved) {
        this.changeTabDialog = true
      } else {
        this.changeTabDialogDefaultFunction()
      }
    },
    onCancelTabChangeDialog () {
      this.changeTabDialog = false
      this.projectWasUpdatedButNotSaved = false
      this.changeTabDialogDefaultFunction()
    },
    onGoBackTabChangeDialog () {
      this.changeTabDialog = false
      this.projectWasUpdatedButNotSaved = false
      this.setIndexTab(this.lastIndexTab)
    },
    getAuditoria (project) {
      return clone({
        usuario: this.user,
        fecha: formatDate(),
        datos: diff(this.immutableProject, project)
      })
    }
  },
  computed: {
    ...mapGetters([ 'apiKey', 'user' ])
  },
  watch: {
    apiKey (val) {
      if (val) this.loadAllDataAndSetCustomValidations()
    },
    project: {
      handler (newVal, oldVal) {
        if (oldVal.code) {
          this.projectWasUpdatedButNotSaved = true
          if (hasChangedObjectValue(diff(this.immutableProject, newVal.general), 'color')) this.hasChangedColor = true
        }
      },
      deep: true
    },
    hasChangedColor (val) {
      if (!this.isCreateView) {
        this.validations.general.shapes = { }
      }
    },
    successDialog (val) {
      if (this.isCreateView && !val) {
        this.$router.push({ name: 'listProject' })
      }
    }
  }
}
</script>

<style scoped lang="stylus">
@import '~@/styles/edit-project-form-style'
</style>
